import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/builder/templates/sentiment-auto-tag/generator.tsx
// Tags every review happy/unhappy. Two modes: rating-only (free) or AI-driven.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { useState, useMemo } from 'react';
import { useReviews, useAI, useToast } from '../../../sdk';
import { Button } from '../../../../ui/Button';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return _jsx(SentimentUi, { name: ctx.instanceName, cfg: cfg });
};
function SentimentUi({ name, cfg }) {
    const { reviews } = useReviews({ limit: 100 });
    const generate = useAI();
    const toast = useToast();
    const [aiTags, setAiTags] = useState({});
    const [busy, setBusy] = useState(false);
    const tags = useMemo(() => {
        const out = {};
        for (const r of reviews ?? []) {
            if (cfg.mode === 'simple') {
                out[r.id] = (r.rating ?? 0) >= cfg.happyThreshold ? 'happy' : 'unhappy';
            }
            else {
                out[r.id] = aiTags[r.id] ?? '?';
            }
        }
        return out;
    }, [reviews, cfg, aiTags]);
    async function tagAll() {
        setBusy(true);
        try {
            for (const r of (reviews ?? []).slice(0, 20)) {
                if (aiTags[r.id])
                    continue;
                const out = await generate({
                    messages: [{ role: 'user', content: `Reply with exactly one word: "happy" or "unhappy". Review: "${r.body ?? ''}"` }],
                    prompt_id: 'plugin:builder:sentiment-auto-tag',
                    max_tokens: 5, temperature: 0,
                });
                const t = out.text.toLowerCase().includes('unhappy') ? 'unhappy' : 'happy';
                setAiTags(s => ({ ...s, [r.id]: t }));
            }
            toast.success('Tagged.');
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'AI tagging failed.');
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("main", { id: "main", className: "mx-auto max-w-3xl px-6 py-10", children: [_jsx("h1", { className: "text-2xl font-semibold", children: name }), _jsxs("p", { className: "mt-1 text-sm text-slate-500", children: ["Mode: ", cfg.mode, cfg.mode === 'simple' ? ` (≥${cfg.happyThreshold}★ = happy)` : '', "."] }), cfg.mode === 'smart' && _jsx("div", { className: "mt-4", children: _jsx(Button, { disabled: busy, onClick: tagAll, children: busy ? 'Tagging…' : 'Tag the latest 20 with AI' }) }), _jsx("ul", { className: "mt-6 space-y-3", children: (reviews ?? []).map(r => (_jsxs("li", { className: "rounded-2xl bg-white p-4 ring-1 ring-slate-200", children: [_jsxs("p", { className: "text-sm font-medium", children: [_jsx("span", { className: 'mr-2 rounded-full px-2 py-0.5 text-xs ' + (tags[r.id] === 'happy' ? 'bg-emerald-100 text-emerald-800' : tags[r.id] === 'unhappy' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-600'), children: tags[r.id] ?? '?' }), r.author_name ?? 'Anonymous', " \u00B7 ", r.rating ?? '?', "\u2605"] }), _jsx("p", { className: "mt-1 text-sm text-slate-600", children: r.body })] }, r.id))) })] }));
}
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Tags each review happy/unhappy using ${cfg.mode} mode.`,
        category: 'utility', icon: 'Tags',
        permissions: ['read:reviews', 'call:ai', 'ui:toast'],
        entry: 'ui',
        config: meta.configSchema,
        defaultConfig: { mode: cfg.mode, happyThreshold: cfg.happyThreshold },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews } from '../sdk';

const MODE = '${esc(cfg.mode)}';
const HAPPY = ${Number(cfg.happyThreshold)};
function tag(r: { rating: number | null; body: string | null }): 'happy' | 'unhappy' {
  if (MODE === 'simple') return (r.rating ?? 0) >= HAPPY ? 'happy' : 'unhappy';
  // Smart mode falls back to rating-based without an AI call when invoked from plain JSX.
  return (r.rating ?? 0) >= HAPPY ? 'happy' : 'unhappy';
}

export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 100 });
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <ul className="mt-6 space-y-3">
        {(reviews ?? []).map(r => {
          const t = tag(r);
          return (<li key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200"><p className="text-sm"><span className={'mr-2 rounded-full px-2 py-0.5 text-xs ' + (t === 'happy' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800')}>{t}</span>{r.author_name ?? 'Anonymous'} · {r.rating ?? '?'}★</p><p className="mt-1 text-sm text-slate-600">{r.body}</p></li>);
        })}
      </ul>
    </main>
  );
}
`;
    return assemble({ manifest, pluginTsx, hooksTs: null,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Auto-tags reviews as happy or unhappy.`,
            configSummary: [['mode', cfg.mode], ['happyThreshold', String(cfg.happyThreshold)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Sentiment tags (${cfg.mode})`,
    describe: (cfg) => `Tag every review happy/unhappy using ${cfg.mode} mode${cfg.mode === 'simple' ? ` (≥${cfg.happyThreshold}★ = happy)` : ''}.`,
    generate, runtimeUi: RuntimeUi,
    clickCount: meta.clickCount,
};
export default template;
